import React, { useState, useEffect } from 'react';
import { FiBriefcase, FiPlus, FiEdit, FiTrash2, FiSave, FiX, FiGlobe, FiUser, FiPackage, FiEye } from 'react-icons/fi';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_ENDPOINTS } from '../../utils/api';

const Manufacturing = () => {
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [viewManufacturer, setViewManufacturer] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    website: '',
    companyDetails: '',
    products: [{ name: '', description: '' }],
    contactPerson: {
      name: '',
      designation: '',
      email: '',
      phone: '',
      alternatePhone: ''
    }
  });

  useEffect(() => {
    fetchManufacturers();
  }, []);

  const fetchManufacturers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(API_ENDPOINTS.getManufacturers, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setManufacturers(response.data);
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
      Swal.fire('Error', 'Failed to fetch manufacturers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactPersonChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      contactPerson: {
        ...prev.contactPerson,
        [name]: value
      }
    }));
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...formData.products];
    updatedProducts[index][field] = value;
    setFormData(prev => ({
      ...prev,
      products: updatedProducts
    }));
  };

  const addProduct = () => {
    setFormData(prev => ({
      ...prev,
      products: [...prev.products, { name: '', description: '' }]
    }));
  };

  const removeProduct = (index) => {
    if (formData.products.length > 1) {
      const updatedProducts = formData.products.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        products: updatedProducts
      }));
    } else {
      Swal.fire('Warning', 'At least one product is required', 'warning');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.companyName.trim()) {
      Swal.fire('Validation Error', 'Manufacturer company name is required', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const manufacturerData = {
        ...formData,
        products: formData.products.filter(p => p.name.trim() !== '')
      };

      if (selectedManufacturer) {
        await axios.put(API_ENDPOINTS.updateManufacturer(selectedManufacturer._id), manufacturerData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Success', 'Manufacturer updated successfully', 'success');
      } else {
        await axios.post(API_ENDPOINTS.createManufacturer, manufacturerData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Success', 'Manufacturer created successfully', 'success');
      }

      setShowModal(false);
      resetForm();
      fetchManufacturers();
    } catch (error) {
      console.error('Error saving manufacturer:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save manufacturer';
      Swal.fire('Error', errorMessage, 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(API_ENDPOINTS.deleteManufacturer(id), {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Deleted', 'Manufacturer has been deleted', 'success');
        fetchManufacturers();
      } catch (error) {
        console.error('Error deleting manufacturer:', error);
        Swal.fire('Error', 'Failed to delete manufacturer', 'error');
      }
    }
  };

  const handleEdit = (manufacturer) => {
    setSelectedManufacturer(manufacturer);
    setFormData({
      companyName: manufacturer.companyName || '',
      website: manufacturer.website || '',
      companyDetails: manufacturer.companyDetails || '',
      products: manufacturer.products?.length > 0 
        ? manufacturer.products 
        : [{ name: '', description: '' }],
      contactPerson: manufacturer.contactPerson || {
        name: '',
        designation: '',
        email: '',
        phone: '',
        alternatePhone: ''
      }
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setSelectedManufacturer(null);
    setFormData({
      companyName: '',
      website: '',
      companyDetails: '',
      products: [{ name: '', description: '' }],
      contactPerson: {
        name: '',
        designation: '',
        email: '',
        phone: '',
        alternatePhone: ''
      }
    });
  };

  const openModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FiBriefcase className="text-blue-600" />
            Manufacturer Companies
          </h1>
          <p className="text-gray-600 mt-1">Manage manufacturer company details, products, and contacts</p>
        </div>
        <button
          onClick={openModal}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FiPlus className="w-5 h-5" />
          Add Manufacturer
        </button>
      </div>

      {/* Manufacturers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : manufacturers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FiBriefcase className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>No manufacturer companies found</p>
            <button
              onClick={openModal}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              Add your first manufacturer
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Website</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Person</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Products</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {manufacturers.map((manufacturer) => (
                  <tr key={manufacturer._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{manufacturer.companyName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {manufacturer.website ? (
                        <a 
                          href={manufacturer.website.startsWith('http') ? manufacturer.website : `https://${manufacturer.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <FiGlobe className="w-4 h-4" />
                          {manufacturer.website}
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {manufacturer.contactPerson?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {manufacturer.contactPerson?.email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {manufacturer.contactPerson?.phone || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div className="flex items-center gap-1">
                        <FiPackage className="w-4 h-4 text-gray-400" />
                        <span>{manufacturer.products?.length || 0} products</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setViewManufacturer(manufacturer);
                          setShowViewModal(true);
                        }}
                        className="text-green-600 hover:text-green-900 mr-4"
                        title="View"
                      >
                        <FiEye className="w-5 h-5 inline" />
                      </button>
                      <button
                        onClick={() => handleEdit(manufacturer)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                        title="Edit"
                      >
                        <FiEdit className="w-5 h-5 inline" />
                      </button>
                      <button
                        onClick={() => handleDelete(manufacturer._id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <FiTrash2 className="w-5 h-5 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">
                {selectedManufacturer ? 'Edit Manufacturer' : 'Add Manufacturer'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Company Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FiBriefcase className="w-5 h-5 text-blue-600" />
                  Company Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Manufacturer Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter company name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                      <FiGlobe className="w-4 h-4" />
                      Website
                    </label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://www.example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Details
                    </label>
                    <textarea
                      name="companyDetails"
                      value={formData.companyDetails}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter company details, description, address, etc."
                    />
                  </div>
                </div>
              </div>

              {/* Products List */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <FiPackage className="w-5 h-5 text-blue-600" />
                    Products List
                  </h3>
                  <button
                    type="button"
                    onClick={addProduct}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    <FiPlus className="w-4 h-4" />
                    Add Product
                  </button>
                </div>
                <div className="space-y-4">
                  {formData.products.map((product, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                        <input
                          type="text"
                          value={product.name}
                          onChange={(e) => handleProductChange(index, 'name', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter product name"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <input
                          type="text"
                          value={product.description}
                          onChange={(e) => handleProductChange(index, 'description', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Product description"
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={() => removeProduct(index)}
                          className="w-full p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          disabled={formData.products.length === 1}
                        >
                          <FiTrash2 className="w-4 h-4 mx-auto" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Person Details */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FiUser className="w-5 h-5 text-blue-600" />
                  Contact Person Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Person Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.contactPerson.name}
                      onChange={handleContactPersonChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Designation
                    </label>
                    <input
                      type="text"
                      name="designation"
                      value={formData.contactPerson.designation}
                      onChange={handleContactPersonChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Sales Manager, Director"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.contactPerson.email}
                      onChange={handleContactPersonChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.contactPerson.phone}
                      onChange={handleContactPersonChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+91 1234567890"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Alternate Phone
                    </label>
                    <input
                      type="tel"
                      name="alternatePhone"
                      value={formData.contactPerson.alternatePhone}
                      onChange={handleContactPersonChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+91 1234567890"
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FiSave className="w-5 h-5" />
                  {selectedManufacturer ? 'Update Manufacturer' : 'Save Manufacturer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewManufacturer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-white">
                Manufacturer Details
              </h2>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewManufacturer(null);
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1 transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Company Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FiBriefcase className="w-5 h-5 text-blue-600" />
                  Company Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Company Name</label>
                    <p className="text-base font-semibold text-gray-900">{viewManufacturer.companyName || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                      <FiGlobe className="w-4 h-4" />
                      Website
                    </label>
                    <p className="text-base text-gray-900">
                      {viewManufacturer.website ? (
                        <a 
                          href={viewManufacturer.website.startsWith('http') ? viewManufacturer.website : `https://${viewManufacturer.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {viewManufacturer.website}
                        </a>
                      ) : '-'}
                    </p>
                  </div>
                  {viewManufacturer.companyDetails && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-500 mb-1">Company Details</label>
                      <p className="text-base text-gray-900 whitespace-pre-wrap">{viewManufacturer.companyDetails}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Products List */}
              {viewManufacturer.products && viewManufacturer.products.length > 0 && (
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FiPackage className="w-5 h-5 text-blue-600" />
                    Products List ({viewManufacturer.products.length})
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border">Product Name</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewManufacturer.products.map((product, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm text-gray-900 border">{product.name}</td>
                            <td className="px-4 py-2 text-sm text-gray-700 border">{product.description || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Contact Person Details */}
              {viewManufacturer.contactPerson && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FiUser className="w-5 h-5 text-blue-600" />
                    Contact Person Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {viewManufacturer.contactPerson.name && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Contact Person Name</label>
                        <p className="text-base font-semibold text-gray-900">{viewManufacturer.contactPerson.name}</p>
                      </div>
                    )}
                    {viewManufacturer.contactPerson.designation && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Designation</label>
                        <p className="text-base text-gray-900">{viewManufacturer.contactPerson.designation}</p>
                      </div>
                    )}
                    {viewManufacturer.contactPerson.email && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                        <p className="text-base text-gray-900">{viewManufacturer.contactPerson.email}</p>
                      </div>
                    )}
                    {viewManufacturer.contactPerson.phone && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Phone</label>
                        <p className="text-base text-gray-900">{viewManufacturer.contactPerson.phone}</p>
                      </div>
                    )}
                    {viewManufacturer.contactPerson.alternatePhone && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Alternate Phone</label>
                        <p className="text-base text-gray-900">{viewManufacturer.contactPerson.alternatePhone}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Manufacturing;
